import type { TableColumnsType } from "antd";
import SlideMenuGuide, { useSlideMenuGuide } from "components/atoms/SlideMenuGuide";
import Tooltip from "components/atoms/Tooltip";
import BatchProcessResult from "components/molecules/BatchProcessResult";
import DataTable from "components/molecules/DataTable";
import moment from "moment";
import React, { useEffect, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DuplicateDataObjectActionButtons from "./DuplicateDataObjectActionButtons";

interface IDuplicateDataObjectJobListProps {
    jobList: IDataObjectsDuplicateJob[];
    onViewData: (j: IDataObjectsDuplicateJob) => void;
    onDeleteJob: (j: IDataObjectsDuplicateJob) => void;
    onCancelJob: (id: string, version: string) => void;
    onRestartJob: (j: IDataObjectsDuplicateJob) => void;
}

const emptyText = "No previous search found.";

const DuplicateDataObjectJobList = ({ jobList, onViewData, onDeleteJob, onCancelJob, onRestartJob }: IDuplicateDataObjectJobListProps) => {
    const [pagination, setPagination] = useState({ defaultPageSize: 100, pageSize: 100, page: 1 });
    const [paginatedJobList, setPaginatedJobList] = React.useState(jobList && jobList.slice(pagination.page - 1, pagination.pageSize));
    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
    const [showMenuGuide, setShowMenuGuide] = React.useState<boolean>(false);
    const { isReady, guidePostion } = useSlideMenuGuide("ant-table-header", "ant-table-row");

    React.useEffect(() => {
        setShowMenuGuide(isReady);
    }, [isReady]);

    useEffect(() => {
        setPaginatedJobList(jobList && jobList.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize));
    }, [jobList, pagination.page, pagination.pageSize]);

    const onPageClick = (page: number, pageSize: number) => {
        let currentPagination = { ...pagination };
        let currentPaginatedJobList = jobList && jobList.slice((page - 1) * pageSize, page * pageSize);
        setPaginatedJobList(currentPaginatedJobList);
        currentPagination.page = page;
        currentPagination.pageSize = pageSize;
        setPagination(currentPagination);
    };

    const onClickRow = (record: IDataObjectsDuplicateJob) => {
        return {
            onClick: () => {
                setSelectedRowKeys([record.jobId]); // Supports single selection only
                onViewData(record);
            },
        };
    };

    const columns: TableColumnsType<IDataObjectsDuplicateJob> = React.useMemo(
        () => [
            {
                fixed: "left",
                width: 40,
                className: "overflow-visible !p-1",
                render: (_v, job) => (
                    <DuplicateDataObjectActionButtons
                        selected={selectedRowKeys.some((a) => a === job.jobId)}
                        job={job}
                        onCancelJob={onCancelJob}
                        onDeleteJob={onDeleteJob}
                        onRestartJob={onRestartJob}
                        onViewData={onViewData}
                    />
                ),
            },
            {
                width: 50,
                title: "No.",
                key: "index",
                render: (value, item, index) => index + 1,
            },
            {
                width: 300,
                dataIndex: "jobId",
                title: "Search Config ID",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: 220,
                dataIndex: "name",
                title: "Name",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: 220,
                dataIndex: "description",
                title: "Description",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value}
                    </Tooltip>
                ),
            },
            {
                width: 150,
                dataIndex: "status",
                title: "Status",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => {
                    let color = "#5C5B5B";
                    switch (value) {
                        case "COMPLETED":
                            color = "#11A400";
                            break;
                        case "FAILED":
                        case "CANCELLING":
                        case "CANCELLED":
                            color = "#FF0000";
                            break;
                        case "RUNNING":
                        case "PENDING":
                            color = "#1F81FF";
                            break;
                        default:
                            break;
                    }
                    let statusText = value;
                    if (job.undo) {
                        if (job.jobType === "FIND_DATA_OBJECTS_DUPLICATES") {
                            statusText = `DELETE ${statusText}`;
                        } else {
                            statusText = `UNDO ${statusText}`;
                        }
                    }
                    return (
                        <Tooltip placement="topLeft" title={statusText}>
                            <span data-testid={CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_STATUS} style={{ color: color }}>
                                {statusText}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                width: 200,
                dataIndex: "runInfo",
                title: "Details",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => {
                    return value ? <BatchProcessResult batchProcessResult={value} textMaxWidth="12rem" readOnly={true}></BatchProcessResult> : <></>;
                },
            },
            {
                width: 200,
                dataIndex: "createdByEmail",
                title: "User",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => (
                    <Tooltip placement="topLeft" title={value}>
                        {value || job.createdBy}
                    </Tooltip>
                ),
            },
            {
                width: 110,
                dataIndex: "startedDate",
                title: "Started Date",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => {
                    let displayValue = null;
                    if (value) {
                        displayValue = moment.utc(value).toDate().toLocaleDateString();
                        return (
                            <Tooltip placement="topLeft" title={displayValue}>
                                {displayValue}
                            </Tooltip>
                        );
                    }
                    return <></>;
                },
            },
            {
                width: 110,
                dataIndex: "startedDate",
                title: "Started Time",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => {
                    let displayValue = null;
                    if (value) {
                        displayValue = moment.utc(value).toDate().toLocaleTimeString();
                        return (
                            <Tooltip placement="topLeft" title={displayValue}>
                                {displayValue}
                            </Tooltip>
                        );
                    }
                    return <></>;
                },
            },
            {
                width: 130,
                dataIndex: "completedDate",
                title: "Completed Date",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => {
                    let displayValue = null;
                    if (value) {
                        displayValue = moment.utc(value).toDate().toLocaleDateString();
                        return (
                            <Tooltip placement="topLeft" title={displayValue}>
                                {displayValue}
                            </Tooltip>
                        );
                    }
                    return <></>;
                },
            },
            {
                width: 130,
                dataIndex: "completedDate",
                title: "Completed Time",
                ellipsis: {
                    showTitle: false,
                },
                render: (value, job, index) => {
                    let displayValue = null;
                    if (value) {
                        displayValue = moment.utc(value).toDate().toLocaleTimeString();
                        return (
                            <Tooltip placement="topLeft" title={displayValue}>
                                {displayValue}
                            </Tooltip>
                        );
                    }
                    return <></>;
                },
            },
            {
                width: 110,
                title: "Elapsed Time",
                ellipsis: {
                    showTitle: false,
                },
                render: (job) => {
                    if (job.startedDate && job.completedDate) {
                        let startDate = moment.utc(job.startedDate);
                        let completedDate = moment.utc(job.completedDate);
                        let duration = moment.duration(completedDate.diff(startDate));
                        let displayValue = moment.utc(duration.as("milliseconds")).format("HH:mm:ss");
                        return (
                            <Tooltip placement="topLeft" title={displayValue}>
                                {displayValue}
                            </Tooltip>
                        );
                    }
                    return <></>;
                },
            },
        ],
        [onCancelJob, onDeleteJob, onRestartJob, onViewData, selectedRowKeys],
    );

    return (
        <DataTable<IDataObjectsDuplicateJob>
            dataTestId={CypressTestIds.DUPLICATE_SEARCH_CONFIG_LIST_TABLE}
            resizable
            hideSelectionColumn
            tableProps={{
                dataSource: paginatedJobList,
                columns: columns,
                locale: emptyText ? { emptyText } : undefined,
                onRow: onClickRow,
                onChange: (pagination, _filters, _sorter, extra) => extra.action === "paginate" && onPageClick && onPageClick(pagination.current ?? 1, pagination.pageSize ?? 100),
                rowSelection: {
                    type: "radio",
                    selectedRowKeys,
                    onChange: (selected) => setSelectedRowKeys(selected),
                },
                rowKey: "jobId",
                rowClassName: "cursor-pointer",
                pagination: {
                    defaultPageSize: 50,
                    pageSize: 50,
                },
            }}
        >
            {paginatedJobList.length > 0 && <SlideMenuGuide open={showMenuGuide} position={guidePostion} onClose={() => setShowMenuGuide(false)} />}
        </DataTable>
    );
};

export default DuplicateDataObjectJobList;
